import { getViewData } from 'assets/js/modules/view-data';
import Cookie from 'js-cookie';
import flattenDeep from 'lodash/flattenDeep';
import get from 'lodash/get';

/**
 * Updates user status and fire mixpanel event
 */
export function logMixpanelSession() {
    if (!window.mixpanel) {
        return;
    }

    const savedUserType = sessionStorage.getItem('userType');
    const savedUserId = sessionStorage.getItem('userId');

    const user = getViewData('user');
    let userType = 'Anonymous';
    const userDistinctId = get(user, 'email');

    const pageName = [...document.body.classList].find((c) => c.startsWith('page-')).replace('page-', '');

    const previouslyUnauthenticatedUser = savedUserType === 'Anonymous' || !savedUserId;
    const hasUpdatedAuthenticationStatus = previouslyUnauthenticatedUser && user.id;

    if (hasUpdatedAuthenticationStatus || !savedUserType) {
        const today = new Date().toISOString();

        mixpanel.people.set_once('A01_First_app_open', today);
        mixpanel.people.increment('A01_Total_app_opens');
    }

    if (user && user.id) {
        userType = user.user_status;
    }

    mixpanel.register({
        A01_Platform: 'Web',
        A01_Page_Name: pageName,
        A01_User_Type: userType,
        A01_Page_Variant: '',
    });

    if (userDistinctId) {
        mixpanel.identify(userDistinctId);
    }

    if (hasUpdatedAuthenticationStatus || !savedUserType) {
        mixpanel.track('AO1');
    }

    if (user && user.id) {
        sessionStorage.setItem('userId', user.id);
    }

    sessionStorage.setItem('userType', userType);
}

/**
 * When the user makes a search, log the search details to mixpanel.
 *
 * @param {object} data
 */
export function logLocationSearch(data) {
    if (!window.mixpanel) {
        return;
    }

    let rental;

    if (data.rental === 'short') {
        rental = 'Hourly';
    } else {
        rental = 'Monthly';
    }

    const eventData = {
        B01_Entry_Point: data.page,
        B01_Parking_Type: rental,
        B01_Entered_destination: data.location,
        B01_Date_from: data.start_date,
        B01_Date_to: data.end_date,
        B01_Search_here: data.search_here,
    };

    mixpanel.people.increment('B01_Number_of_searches_made');

    mixpanel.track('B01', eventData);
}

/**
 * Log when a user views a location.
 *
 * @param {object} data
 */
export function logLocationView(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        'C01_Entry_Point:_Parking_Space_Views': data.page,
        C01_Name: data.title,
        C01_ID: data.id,
        C01_User_Distance: -1,
        C01_Search_Distance: data.distance,
        C01_Review_count: data.review_count,
        C01_Review_score: data.review_average,
        C01_Price: data.price,
    };

    mixpanel.people.increment('C01_Incremental_parking_spaces_views');

    mixpanel.track('C01', eventData);
}

/**
 * logs when a user clicks on a map pin.
 */
export function logMapClick() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('C02');
}

/**
 * Authenticates user in the Mixpanel session.
 *
 * @param {object} data
 * @param {string} data.email
 */
export function authenticateUserInMixpanel({ email }) {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.register({
        A01_User_Type: getViewData('user.user_status'),
    });

    mixpanel.identify(email);
}

/**
 * Logs when a user logs in.
 *
 * @param {object} data
 */
export function logLoginEvent(data) {
    if (!window.mixpanel) {
        return;
    }

    Cookie.set('login_type', data.type);

    const eventData = {
        G01_login: new Date().toString(),
        G01_Type_of_login: data.type,
    };

    mixpanel.track('G01', eventData);
}

/**
 * Log checkout view of short term booking.
 *
 * @param {object} data
 */
export function logConfirmReservationDaily(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        'D01_Entry_Point:_Review_Your_Reservation': data.referrer,
        D01_Location: data.title,
        D01_Arriving_On_1: data.start_date,
        D01_Departing_On_1: data.end_date,
        D01_Duration_1: data.duration,
        D01_Price_of_parking_1: data.price,
        D01_Location_ID: data.id,
        D01_Type_of_parking_space: data.space_type,
        D01_Space_features: data.features,
        D01_Parking_space_description: data.description,
    };

    mixpanel.track('D01', eventData);
}

/**
 * log when a user adds a multi day booking
 *
 * @param {string} date
 */
export function logAdditionalDateAdded(date) {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('d02', { date });
}

/**
 * Log when a user removes a date from a multi day booking.
 */
export function logAdditionalDateRemoved() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('d03');
}

/**
 * Log checkout view of monthly booking.
 *
 * @param {object} data
 */
export function logConfirmReservationMonthly(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        'E01_Entry_Point:_Review_Your_Reservation': document.referrer,
        E01_Location: data.title,
        E01_Start_Date: data.start_date,
        E01_Total_Price: data.value,
        E01_Location_ID: data.id,
        E01_Type_of_parking_space: data.space_type,
        E01_Space_features: data.features,
        E01_Parking_space_description: data.description,
    };

    mixpanel.track('E01', eventData);
}

/**
 * Log when a user adds a new vehicle
 */
export function logAddVehicleShort() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('d04');
}

/**
 * Log when a user adds a new vehicle
 */
export function logAddVehicleLong() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('e02');
}

/**
 * Log when a user registers with their email
 *
 * @param {object} data
 */
export function logEmailRegistration(data) {
    if (!window.mixpanel) {
        return;
    }

    const pageName = [...document.body.classList].find((c) => c.startsWith('page-')).replace('page-', '');

    const eventData = {
        'F01_Entry_Point:_Registration': pageName,
    };

    mixpanel.identify(data.email);

    mixpanel.people.set({
        'F01_Login_Type': data.provider,
        'F01_First_Name': data.first_name,
        'F01_Surname': data.last_name,
        'F01_Email': data.email,
        'F01_Phone_Number': data.mobile,
        'F01_Newsletter_Sign-up': data.marketing_consent_given,
        'F01_Complete_registration_date': new Date().toString(),
        'F01_Referred?': false,
    });

    mixpanel.track('F01', eventData);
}

/**
 * Log when a user registers with their social media
 *
 * @param {object} data
 */
export function logSocialRegistration(data) {
    if (!window.mixpanel) {
        return;
    }

    const pageName = [...document.body.classList].find((c) => c.startsWith('page-')).replace('page-', '');

    const eventData = {
        'F01_Entry_Point:_Registration': pageName,
    };

    let { firstName, lastName } = data;

    if (data.name) {
        let [first, ...last] = data.name.split(' ');

        firstName = first;
        lastName = last.join(' ');
    }

    mixpanel.identify(data.email);

    mixpanel.people.set({
        'F01_Login_Type': data.provider,
        'F01_First_Name': firstName,
        'F01_Surname': lastName,
        'F01_Email': data.email,
        'F01_Phone_Number': '', // social auth does not scrape for a mobile number
        'F01_Newsletter_Sign-up': false,
        'F01_Complete_registration_date': new Date().toString(),
        'F01_Referred?': false,
    });

    mixpanel.track('F01', eventData);
}

/**
 * Logs when the user clicks proceed to payment/checkout on reservation screen.
 *
 * @param {object} data
 */
export function logProceedToCheckout(data) {
    if (!window.mixpanel) {
        return;
    }

    let frequency = '0';

    if (data.rental === 'long') {
        frequency = '1 month';
    }

    if (data.isSeason && data.seasonIntervalData ) {
        const frequencyValue = data.seasonIntervalData.billing.interval_count;

        frequency = `${ frequencyValue } month`;

        if (frequencyValue > 1) {
            frequency = `${ frequencyValue } months`;
        }
    }

    const eventData = {
        H01_Proceeds_to_Payment: new Date().toString(),
        H01_Billing_Frequency: frequency,
    };

    const user = getViewData('user');
    const email = user.email;

    mixpanel.identify(email);

    const reg = data.vehicle;

    mixpanel.people.set({
        H01_Vehicle: reg,
    });

    mixpanel.track('H01', eventData);
}

/**
 * Logs when a successful payment is made
 *
 * @param {object} data
 */
export function logPaymentSuccess(data) {
    if (!window.mixpanel) {
        return;
    }

    let rental;

    if (data.rental === 'short') {
        rental = 'Hourly';
    } else {
        rental = 'Monthly';
    }

    const userType = Cookie.get('login_type');

    const eventData = {
        I01_Payment_Type: data.payment_method,
        I01_Booking_Type: rental,
        I01_Booking_Location: data.location,
        I01_Total_cost_of_booking: data.quoteValue,
        I01_Discount_Amount: data.discountValue,
        I01_Registration_Type: userType,
        I01_First_Booking: data.firstBooking,
    };

    const user = getViewData('user');
    const email = user.email;

    mixpanel.identify(email);

    mixpanel.people.increment('I01_Total_number_of_bookings', 1);

    mixpanel.people.set_once({
        I01_Date_of_First_Booking: new Date().toString(),
    });

    mixpanel.track('I01', eventData);
}

/**
 * Logs when a payment fails
 */
export function logPaymentFailure() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('i02');
}

/**
 * Log when a user attempts cancels their bookings
 *
 * @param {object} data
 */
export function logBookingCancellationRequest(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        J01_Booking_Cancel_Request_Date: new Date().toString(),
        J01_Booking_Cancel_Request_Type: data.selectedReason,
    };

    mixpanel.track('J01', eventData);
}

/**
 * Log when a booking is cancelled
 */
export function logBookingCancellationSuccess() {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        J02_Booking_Cancelled: new Date().toString(),
    };

    mixpanel.track('j02', eventData);
}

/**
 * Log when a user begins listing a new space
 */
export function logListASpaceClicked() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.people.set({
        'L01_List_Your_Space_clicked': new Date().toString(),
    });

    mixpanel.track('L01');
}

/**
 * Log when a listing is completed or updated
 *
 * @param {object} data
 */
export function logListingComplete(data) {
    if (!window.mixpanel) {
        return;
    }

    const availability = data.availability / 30;
    const months = Math.round(availability);

    const eventData = {
        L02_Type_of_parking_space: data.type,
        L02_Space_features: data.features,
        L02_Parking_space_description: data.description,
        L02_Vehicle_size: data.size,
        L02_Location_of_space: data.location,
        L02_Access_Type: data.access_type,
        L02_Space_Availability: `${ months } months`,
        'L02_Account_Manager_(YPS)': '',
        L02_Commercial_Group: '',
        L02_Space_Sector: 'private',
    };

    mixpanel.track('L02', eventData);
}

/**
 * Log when a user updates their space
 */
export function logListingUpdated() {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        L03_last_updated: new Date().toString(),
    };

    mixpanel.track('L03', eventData);
}

/**
 * Log when a user disables their space
 */
export function logSpaceWithdrawn() {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        L04_Space_Withdrawn: new Date().toString(),
    };

    mixpanel.track('L04', eventData);
}

/**
 * Log when a user hits any of the SEO location list pages.
 */
export function logListPageViews() {
    if (!window.mixpanel) {
        return;
    }

    const pageName = [...document.body.classList].find((c) => c.startsWith('page-hatchlypagessystem')).replace('page-hatchlypagessystem', '');

    const user = getViewData('user');
    const email = user.email;

    mixpanel.identify(email);

    switch(pageName) {
        case 'towns-and-cities':
            mixpanel.people.set('n01_Landed_on_Towns_&_Cities', new Date().toString());
            break;
        case 'all-london-areas':
            mixpanel.people.set('n02_Landed_on_London_Areas', new Date().toString());
            break;
        case 'airport-parking':
            mixpanel.people.set('n03_Landed_on_Airports', new Date().toString());
            break;
        case 'all-train-stations':
            mixpanel.people.set('n04_Landed_on_Train_Stations', new Date().toString());
            break;
        case 'stadiums-and-venues':
            mixpanel.people.set('n05_Landed_on_Stadiums', new Date().toString());
    }
}

/**
 * Log when a user clicks into my account area
 */
export function logAccountButtonClick() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('M01_Clicks_on_My_Account');
}

/**
 * Log when the account landlord page is viewed
 */
export function logSpaceOwnerView() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m02_Landed_on_Space_Owners');
}

/**
 * Log when the user edits their space and saves
 */
export function logSpaceListingEdit() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m03_Edit_Space_Listing');
}

/**
 * Log when a space owner disabled a space
 */
export function logSpaceDisabled(data) {
    if (!window.mixpanel) {
        return;
    }

    const user = getViewData('user');
    const email = user.email;

    mixpanel.identify(email);

    mixpanel.people.set({
        'm03_Disable_Space_Listing': new Date().toString(),
    });

    if (data.bookingCount) {
        logSpaceOwnerCancellations(data.bookingCount);
    }
}

/**
 * Log when a space owner cancels a booking at one of their sites
 */
export function logSpaceOwnerCancellations(count) {
    if (!window.mixpanel) {
        return;
    }

    let bookings = 1;

    if (count) {
        bookings = count;
    }

    const user = getViewData('user');
    const email = user.email;

    mixpanel.identify(email);
    mixpanel.people.increment('m02_Number_of_bookings_cancelled_', bookings);
}

/**
 * Log when a user updates which vehicle is their default
 */
export function logUpdateDefaultVehicle() {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        m03_Update_default_vehicle: new Date().toString(),
    };

    mixpanel.track('M04', eventData);
}

/**
 * Log when a user adds a new payment method to their account
 */
export function logDriverPaymentAdded() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m05_Payment_Method_Added');
}

/**
 * Log when a driver adds a new payment method to their account
 */
export function logPaymentMethodAdded() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m03B_Payment_Method_Added');
}

/**
 * Log when a user adds a referral code to their account
 *
 * @param {object} data
 */
export function logReferralCodeAdded(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        m07_Referral_Code_Discount: data.value,
        m07_Referral_Code_Added_Date: new Date().toString(),
        m07_Referral_Code: data.code,
    };

    mixpanel.track('m07', eventData);
}

/**
 * Log when a user successfully verifies their mobile number
 */
export function logPhoneNumberVerified() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m08_Phone_Number_Verified');
}

/**
 * Log when a user requests directions in a booking
 */
export function logRequestedDirection() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m09_Directions_Requested');
}

/**
 * Log when a user adds a referral code to their account
 */
export function logBookingCalendar() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m10_Booking_added_to_calendar');
}

/**
 * Log when a user adds a referral code to their account
 *
 * @param {object} data
 */
export function logSpaceReview(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        m11_Space_review_left_Date: new Date().toString(),
        m11_Space_Review_Left_Rating: data.rating,
    };

    mixpanel.track('m11', eventData);
}

/**
 * Log when a user uses the quick rebook feature
 */
export function logQuickRebook() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m12_Quick_Re-book');
}

/**
 * Log when a user lodges a booking complaint on a past booking
 */
export function logBookingComplaint() {
    if (!window.mixpanel) {
        return;
    }

    mixpanel.track('m13_Booking_Complaint_Made');
}
/**
* Log when a user contacts a space owner about their space
*/
export function logContactSpaceOwner(data) {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        O01_Contact_Space_Owner_Date: new Date().toString(),
        O01_Contact_Space_Owner_Type: data.type,
    };

    mixpanel.track('O01', eventData);
}

/**
 * Log when a user contacts customer service
 */
export function logContactCustomerService() {
    if (!window.mixpanel) {
        return;
    }

    const eventData = {
        O02_Customer_Service_Date_Date: new Date().toString(),
        O02_Customer_Service_Type: 'web',
    };

    mixpanel.track('O02', eventData);
}

/**
 * Log changes to a users communication preferences
 *
 * @param {object} data
 */
export function logCommsChange(data) {
    if (!window.mixpanel) {
        return;
    }

    const user = getViewData('user');
    const email = user.email;

    mixpanel.identify(email);

    const types = data.types;

    const eventData = {};
    const bookingConfirmation = types['booking-confirmation-landlord[]'] || types['booking-confirmation-tenant[]'];
    const bookingReminder = types['booking-reminder-landlord[]'] || types['booking-reminder-tenant[]'];
    const generalInformation = types['general[]'];

    if (bookingConfirmation) {
        eventData.O03_Transactional_Communications_Changed_Date = new Date().toString();

        mixpanel.people.set('O03_Transactional_Communications', true);
    } else {
        mixpanel.people.set('O03_Transactional_Communications', false);
    }

    if (bookingReminder) {
        eventData.O03_Booking_Reminder_Changed_Date = new Date().toString();

        const listBookingOfReminder = flattenDeep([bookingReminder]);

        mixpanel.people.set('O03_Booking_Reminder_Type', listBookingOfReminder);
    } else {
        mixpanel.people.set('O03_Booking_Reminder_Type', []);
    }

    if (generalInformation) {
        eventData.O03_Mailing_List_Changed_Date = new Date().toString();

        const listOfGeneralInformation = flattenDeep([generalInformation]);

        mixpanel.people.set('O03_Mailing_List_Type', listOfGeneralInformation);
    } else {
        mixpanel.people.set('O03_Mailing_List_Type', []);
    }

    mixpanel.track('O03', eventData);
}

